/* eslint-disable max-lines */
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  Radio,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { casingFormat } from 'common/utils';
import { formatDistanceToNow, isValid } from 'date-fns';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

const formatCurrentAddress = (address = {}) => {
  const { line1, line2, city, state, zip } = address;
  const street = [line1, line2].filter(Boolean).join(', ');
  const region = [[city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ');
  return { street, region };
};

const formatLastUsed = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return isValid(date) ? formatDistanceToNow(date, { addSuffix: true }) : '';
};

// Initials for the recipient avatar, e.g. "Brown, Liam" / "Liam Brown" -> "BL"/"LB".
const getInitials = (name) =>
  (name || '')
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

// Normalized content key so the current Ship To (from the shipment service — a different
// id-space) can be matched to a saved address by value. Zip is reduced to its first 5 digits.
const addressKey = (address = {}) => {
  const { line1, line2, city, state, zip } = address;
  return [line1, line2, city, state, zip]
    .map((part) =>
      String(part || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
    )
    .join('|');
};

const CategoryChips = ({ patientAddress }) => {
  const isDefault = (patientAddress.tags || []).some((tag) => tag?.toUpperCase() === 'DEFAULT');
  return (
    <>
      {patientAddress.isBusinessAddress && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            }}
          >
            <BusinessIcon sx={{ fontSize: '0.95rem' }} />
          </Box>
          <Typography variant='body2' fontWeight={700}>
            Business
          </Typography>
        </Box>
      )}
      {isDefault && (
        <Chip
          size='small'
          label='Default'
          variant='outlined'
          sx={{
            height: 20,
            fontSize: '0.6875rem',
            fontWeight: 600,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      )}
    </>
  );
};

CategoryChips.propTypes = {
  patientAddress: PropTypes.shape({
    isBusinessAddress: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const RecipientAddressRow = ({ patientAddress, isCurrent, isSelected, onSelect }) => {
  const { address } = patientAddress;
  const { street, region } = formatCurrentAddress(address);
  const lastUsed = formatLastUsed(patientAddress.lastUsedTimestamp);

  return (
    <Box
      onClick={onSelect}
      role='radio'
      aria-checked={isSelected}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.75,
        py: 1.5,
        mb: 1.25,
        cursor: 'pointer',
        borderRadius: 2,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        transition: 'border-color 150ms ease',
        '&:hover': { borderColor: isSelected ? 'primary.main' : 'text.disabled' },
        '&:last-of-type': { mb: 0 },
      }}
    >
      <Radio size='small' checked={isSelected} tabIndex={-1} sx={{ p: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body2' fontWeight={600} noWrap>
          {street}
        </Typography>
        <Typography variant='body2' color='text.secondary' noWrap>
          {region}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {isCurrent && (
            <Chip
              size='small'
              label='Current'
              color='success'
              variant='outlined'
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          <CategoryChips patientAddress={patientAddress} />
        </Box>
        {lastUsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <HistoryIcon sx={{ fontSize: '0.875rem', color: 'text.disabled' }} />
            <Typography variant='caption' color='text.secondary' noWrap>
              Last used {lastUsed}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

RecipientAddressRow.propTypes = {
  patientAddress: PropTypes.shape({
    patientAddressId: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    isBusinessAddress: PropTypes.bool,
    isVerified: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string),
    lastUsedTimestamp: PropTypes.string,
  }).isRequired,
  isCurrent: PropTypes.bool,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

RecipientAddressRow.defaultProps = {
  isCurrent: false,
};

const SelectPatientAddressDialog = ({
  open,
  onClose,
  recipientAddresses,
  isLoading,
  error,
  isSaving,
  currentShipTo,
  onUseAddress,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const currentAddressKey = useMemo(
    () => (currentShipTo?.address ? addressKey(currentShipTo.address) : ''),
    [currentShipTo]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setCollapsed({});
  }, [open]);

  // Pre-select the saved address matching the shipment's current Ship To (matched by
  // normalized content, since the two live in different id-spaces). Only sets the initial
  // value — a manual pick is preserved.
  useEffect(() => {
    if (!open || !currentAddressKey) return;
    const match = recipientAddresses
      .flatMap((recipient) => recipient.addresses || [])
      .find((addr) => addressKey(addr.address) === currentAddressKey);
    if (match) setSelectedId((prev) => prev || match.patientAddressId);
  }, [open, recipientAddresses, currentAddressKey]);

  const groups = useMemo(
    () =>
      recipientAddresses
        .filter((recipient) => !isEmpty(recipient.addresses))
        .map((recipient) => ({
          ...recipient,
          // Bubble the current (in-use) address to the top of each recipient's list.
          addresses: currentAddressKey
            ? [...recipient.addresses].sort(
                (a, b) =>
                  (addressKey(a.address) === currentAddressKey ? 0 : 1) -
                  (addressKey(b.address) === currentAddressKey ? 0 : 1)
              )
            : recipient.addresses,
        })),
    [recipientAddresses, currentAddressKey]
  );

  const selectedAddress = useMemo(
    () =>
      recipientAddresses
        .flatMap((recipient) =>
          (recipient.addresses || []).map((address) => ({
            ...address,
            mpi: recipient.mpi,
            patientName: recipient.patientName,
          }))
        )
        .find((address) => address.patientAddressId === selectedId) || null,
    [recipientAddresses, selectedId]
  );

  // Confirming the address already on the shipment is a no-op — block it.
  const isSelectedCurrent =
    !!selectedAddress &&
    !!currentAddressKey &&
    addressKey(selectedAddress.address) === currentAddressKey;

  const toggleGroup = (mpi) => setCollapsed((prev) => ({ ...prev, [mpi]: !prev[mpi] }));

  const renderSkeleton = () =>
    [0, 1].map((group) => (
      <Box
        key={group}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Skeleton variant='text' width={160} />
        </Box>
        {[0, 1].map((row) => (
          <Box
            key={row}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 2,
              py: 1.5,
              borderTop: row === 0 ? 0 : '1px solid',
              borderColor: 'divider',
            }}
          >
            <Skeleton variant='circular' width={20} height={20} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant='text' width='60%' />
              <Skeleton variant='text' width='40%' />
            </Box>
            <Skeleton variant='rounded' width={70} height={22} />
          </Box>
        ))}
      </Box>
    ));

  const renderEmpty = (Icon, message) => (
    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
      <Icon sx={{ fontSize: '2.25rem', opacity: 0.4, mb: 1 }} />
      <Typography variant='body2'>{message}</Typography>
    </Box>
  );

  const renderBody = () => {
    if (isLoading) return renderSkeleton();
    if (error)
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity='error'>{error}</Alert>
        </Box>
      );
    if (isEmpty(groups)) {
      return renderEmpty(LocationOnIcon, 'No saved addresses found for this shipment.');
    }

    const isSingleRecipient = groups.length === 1;

    return groups.map((recipient) => {
      // Collapsing only earns its keep with multiple recipients (batch shipments);
      // with a single recipient the group is static and always expanded.
      const isOpen = isSingleRecipient || !collapsed[recipient.mpi];
      const count = recipient.addresses.length;
      const initials = getInitials(recipient.patientName);
      const displayName = recipient.patientName ? casingFormat(recipient.patientName) : 'Recipient';
      return (
        <Box
          key={recipient.mpi}
          sx={{
            mb: 2.5,
            p: 1,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { mb: 0 },
          }}
        >
          {/* Recipient header — grouped with its address cards inside one container */}
          <Box
            onClick={isSingleRecipient ? undefined : () => toggleGroup(recipient.mpi)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.25,
              py: 1,
              mb: 1,
              borderRadius: 2,
              cursor: isSingleRecipient ? 'default' : 'pointer',
              ...(!isSingleRecipient && { '&:hover': { bgcolor: 'action.hover' } }),
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              }}
            >
              {initials ? (
                <Typography
                  variant='caption'
                  fontWeight={700}
                  color='primary.main'
                  sx={{ fontSize: '0.8125rem', lineHeight: 1, letterSpacing: 0.2 }}
                >
                  {initials}
                </Typography>
              ) : (
                <PersonIcon sx={{ fontSize: '1.1rem' }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.625rem',
                  lineHeight: 1.4,
                }}
              >
                Recipient
              </Typography>
              <Typography variant='subtitle2' fontWeight={700} color='primary.main' noWrap>
                {displayName}
              </Typography>
            </Box>
            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: 'action.hover', flexShrink: 0 }}>
              <Typography variant='caption' fontWeight={600} color='text.secondary'>
                {count} {count === 1 ? 'Address' : 'Addresses'}
              </Typography>
            </Box>
            {!isSingleRecipient &&
              (isOpen ? (
                <ExpandLessIcon sx={{ color: 'action.active' }} />
              ) : (
                <ExpandMoreIcon sx={{ color: 'action.active' }} />
              ))}
          </Box>
          <Collapse in={isOpen} unmountOnExit>
            {recipient.addresses.map((patientAddress) => (
              <RecipientAddressRow
                key={patientAddress.patientAddressId}
                patientAddress={patientAddress}
                isCurrent={
                  !!currentAddressKey && addressKey(patientAddress.address) === currentAddressKey
                }
                isSelected={patientAddress.patientAddressId === selectedId}
                onSelect={() => setSelectedId(patientAddress.patientAddressId)}
              />
            ))}
          </Collapse>
        </Box>
      );
    });
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      // Sits above the full-screen Shipment Details Dialog (which owns the modal layer).
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      // Gentle, smooth slide-in from the right (soft ease-out); quicker on exit.
      transitionDuration={{ enter: 420, exit: 280 }}
      SlideProps={{
        easing: {
          enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
          exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant='h6' fontWeight={700} sx={{ flex: 1 }}>
            Select Recipient Address
          </Typography>
          <IconButton onClick={onClose} size='small' aria-label='Close' disabled={isSaving}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
          Choose an existing address from the recipient&apos;s saved addresses.
        </Typography>
        {currentShipTo?.address && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <LocationOnIcon sx={{ fontSize: '1.1rem', color: 'primary.main', mt: 0.25 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant='caption'
                fontWeight={700}
                color='text.secondary'
                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
              >
                Current Ship To
              </Typography>
              {currentShipTo.recipientName && (
                <Typography variant='body2' fontWeight={600}>
                  {casingFormat(currentShipTo.recipientName)}
                </Typography>
              )}
              <Typography variant='body2' color='text.secondary'>
                {[
                  formatCurrentAddress(currentShipTo.address).street,
                  formatCurrentAddress(currentShipTo.address).region,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Scrollable content: address list + note flow together (keeps content top-aligned,
          avoids a floating note with a large empty gap above it) */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>{renderBody()}</Box>

      {/* Pinned footer: note + actions stay at the bottom of the drawer */}
      <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
        {!isLoading && !error && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              mb: 1.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: '1.25rem', color: 'info.main', flexShrink: 0, mt: 0.1 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant='caption'
                fontWeight={700}
                color='text.primary'
                sx={{ display: 'block' }}
              >
                Please note
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.45 }}>
                The selected address will replace the shipment&apos;s current{' '}
                <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Ship&nbsp;To
                </Box>{' '}
                when you confirm.
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={onClose} variant='outlined' color='inherit' disabled={isSaving}>
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            loading={isSaving}
            disabled={!selectedAddress || isSelectedCurrent}
            onClick={() => onUseAddress(selectedAddress)}
          >
            Confirm
          </LoadingButton>
        </Box>
      </Box>
    </Drawer>
  );
};

SelectPatientAddressDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  recipientAddresses: PropTypes.arrayOf(
    PropTypes.shape({
      mpi: PropTypes.string,
      patientName: PropTypes.string,
      addresses: PropTypes.arrayOf(PropTypes.shape({ patientAddressId: PropTypes.string })),
    })
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  isSaving: PropTypes.bool,
  currentShipTo: PropTypes.shape({
    recipientName: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
  }),
  onUseAddress: PropTypes.func.isRequired,
};

SelectPatientAddressDialog.defaultProps = {
  open: false,
  recipientAddresses: [],
  isLoading: false,
  error: '',
  isSaving: false,
  currentShipTo: null,
};

export default SelectPatientAddressDialog;
